import { Autocomplete, FormField } from '@dev-spendesk/grapes';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';

import { NoOptions } from './NoOptions';
import { type AnalyticalFieldMappingV2 } from '../../../bookkeep/settings/accounting';
import { type SyncMode } from '../../apis/useSetSyncSettings';

type Props = {
  fieldName: string;
  fields: AnalyticalFieldMappingV2[];
  fieldMapping?: AnalyticalFieldMappingV2 | undefined;
  onValueSelected: (field: AnalyticalFieldMappingV2 | undefined) => void;
  hasError?: boolean;
  syncMode?: SyncMode;
};

type Option = AnalyticalFieldMappingV2 & {
  key: string;
  label: string;
};

const mapOption = (field: AnalyticalFieldMappingV2): Option => ({
  ...field,
  key: field.id,
  label: field.externalName,
});

export const AnalyticalFieldSelector = ({
  fields,
  fieldName,
  fieldMapping,
  onValueSelected,
  hasError,
  syncMode,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedValue, setSelectedValue] = useState<Option>();

  const notMappedFields = useMemo(
    () => fields.filter((field) => !field.internalId),
    [fields],
  );

  useEffect(() => {
    setOptions(notMappedFields.map(mapOption));
  }, [notMappedFields]);

  useEffect(() => {
    if (fieldMapping) {
      setSelectedValue(mapOption(fieldMapping));
    }
  }, [fieldMapping]);

  const handleSearch = (value: string | undefined) => {
    if (!value) {
      setOptions(notMappedFields.map(mapOption));
      return;
    }

    setOptions((previous) =>
      previous.filter(({ label }) =>
        label.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  };

  const handleChange = useCallback(
    (option: Option | undefined) => {
      setSelectedValue(option);

      if (option?.key !== selectedValue?.key) {
        onValueSelected(option);
      }
    },
    [selectedValue],
  );

  return (
    <div>
      <FormField
        alertMessage={
          hasError
            ? t(
                'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.validation.selectField',
                {
                  integration: accountingSoftwareName,
                },
              )
            : undefined
        }
        label={
          syncMode && syncMode === 'pull'
            ? t(
                'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.fieldPullLabel',
                { integration: accountingSoftwareName, fieldName },
              )
            : t(
                'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.fieldMappingLabel',
                { integration: accountingSoftwareName, fieldName },
              )
        }
      >
        <Autocomplete<Option>
          fit="parent"
          options={options}
          value={selectedValue}
          onSearch={handleSearch}
          onSelect={handleChange}
          showClearSelectionButton
          placeholder={t(
            'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.autoCompletePlaceholder',
          )}
          renderNoOptions={() => <NoOptions isFieldSelector />}
        />
      </FormField>
    </div>
  );
};
